/** Pure helpers for amounts in legal documents: "$1,500.50" and "One thousand five hundred dollars and 50/100". */

const ONES = ["zero", "one", "two", "three", "four", "five", "six", "seven", "eight", "nine", "ten", "eleven", "twelve", "thirteen", "fourteen", "fifteen", "sixteen", "seventeen", "eighteen", "nineteen"];
const TENS = ["", "", "twenty", "thirty", "forty", "fifty", "sixty", "seventy", "eighty", "ninety"];
const SCALES = ["", " thousand", " million", " billion"];

function below1000(n: number): string {
  const parts: string[] = [];
  if (n >= 100) { parts.push(`${ONES[Math.floor(n / 100)]} hundred`); n %= 100; }
  if (n >= 20) { parts.push(n % 10 ? `${TENS[Math.floor(n / 10)]}-${ONES[n % 10]}` : TENS[Math.floor(n / 10)]); }
  else if (n > 0 || parts.length === 0) parts.push(ONES[n]);
  return parts.join(" ");
}

/** Whole number below one trillion in English words, lower case. */
export function integerInWords(n: number): string {
  if (n === 0) return "zero";
  const out: string[] = [];
  let scale = 0;
  while (n > 0) {
    const chunk = n % 1000;
    if (chunk) out.unshift(`${below1000(chunk)}${SCALES[scale]}`);
    n = Math.floor(n / 1000);
    scale++;
  }
  return out.join(" ");
}

/** "$1,500.50". Always two decimals so the cents are never ambiguous. */
export function formatUsd(n: number): string {
  return `$${n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/** "One thousand five hundred dollars and 50/100"; empty string for amounts that are not usable. */
export function amountInWords(n: number): string {
  if (!Number.isFinite(n) || n < 0 || n >= 1e12) return "";
  const cents = Math.round(n * 100);
  const dollars = Math.floor(cents / 100);
  const rest = cents % 100;
  const words = integerInWords(dollars);
  const text = `${words} ${dollars === 1 ? "dollar" : "dollars"} and ${String(rest).padStart(2, "0")}/100`;
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/** "$1,500.50 (One thousand five hundred dollars and 50/100)" for use inside a clause. */
export function amountWithWords(n: number): string {
  const w = amountInWords(n);
  return w ? `${formatUsd(n)} (${w})` : formatUsd(n);
}

/** "1 year" / "3 years". */
export function yearsText(n: number): string {
  return `${n} ${n === 1 ? "year" : "years"}`;
}

/** Error text when `s` is not a whole number of years from 1 to 99, else null. */
export function wholeYearsMessage(s: string, label: string): string | null {
  const t = s.trim();
  if (!t) return `${label} is required.`;
  if (!/^\d+$/.test(t)) return `${label} must be a whole number of years, 1 or more.`;
  const n = Number(t);
  if (n < 1 || n > 99) return `${label} must be between 1 and 99 years.`;
  return null;
}

/** Error text when `n` (already parsed, null if unreadable) is not a positive amount, else null. Empty input gives null. */
export function positiveAmountMessage(raw: string, n: number | null, label: string): string | null {
  if (!raw.trim()) return null;
  if (n === null) return `${label} must be a number, for example 1,500.50.`;
  if (n <= 0) return `${label} must be more than 0.`;
  if (n >= 1e12) return `${label} is too large.`;
  return null;
}
